import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import Home from './pages/Home';
import VisaAgent from './pages/VisaAgent';
import PrivacyPolicy from './pages/PrivacyPolicy';
import DeleteAccount from './pages/DeleteAccount';

function App() {
  const globalTheme = `
    @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Space+Grotesk:wght@500;700&display=swap');

    :root {
      --page-bg: #09111f;
      --surface: rgba(11, 21, 40, 0.72);
      --surface-strong: rgba(12, 24, 46, 0.92);
      --surface-soft: rgba(143, 222, 255, 0.08);
      --text-primary: #f5f7fb;
      --text-secondary: #a9b7d0;
      --line: rgba(169, 183, 208, 0.18);
      --accent: #83e1ff;
      --accent-strong: #4cc8ff;
      --accent-warm: #ffd37a;
      --shadow-xl: 0 30px 90px rgba(0, 0, 0, 0.35);
      --shadow-card: 0 18px 50px rgba(5, 10, 20, 0.32);
      --radius-xl: 32px;
      --radius-lg: 24px;
      --radius-md: 18px;
      --max-width: 1180px;
    }

    * {
      box-sizing: border-box;
    }

    html {
      scroll-behavior: smooth;
    }

    body {
      min-height: 100vh;
      margin: 0;
      font-family: 'Manrope', sans-serif;
      color: var(--text-primary);
      background:
        radial-gradient(circle at top left, rgba(76, 200, 255, 0.24), transparent 30%),
        radial-gradient(circle at top right, rgba(255, 211, 122, 0.18), transparent 28%),
        linear-gradient(180deg, #08101d 0%, #0b1324 52%, #09111f 100%);
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }

    a {
      color: inherit;
      text-decoration: none;
    }

    button,
    input,
    textarea {
      font: inherit;
    }

    .page-shell {
      position: relative;
      overflow: hidden;
      isolation: isolate;
    }

    .page-shell::before,
    .page-shell::after {
      content: '';
      position: fixed;
      inset: auto;
      border-radius: 999px;
      filter: blur(60px);
      opacity: 0.55;
      pointer-events: none;
      z-index: -1;
    }

    .page-shell::before {
      width: 320px;
      height: 320px;
      top: 72px;
      left: -80px;
      background: rgba(76, 200, 255, 0.26);
    }

    .page-shell::after {
      width: 280px;
      height: 280px;
      right: -60px;
      bottom: 120px;
      background: rgba(255, 211, 122, 0.18);
    }

    .site-section {
      width: min(var(--max-width), calc(100% - 32px));
      margin: 0 auto;
    }

    .section-kicker {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      padding: 8px 14px;
      border: 1px solid rgba(131, 225, 255, 0.18);
      border-radius: 999px;
      background: rgba(131, 225, 255, 0.08);
      color: var(--accent);
      font-size: 0.78rem;
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
    }

    .section-kicker::before {
      content: '';
      width: 7px;
      height: 7px;
      border-radius: 999px;
      background: var(--accent);
      box-shadow: 0 0 18px var(--accent);
    }

    .display-title {
      font-family: 'Space Grotesk', sans-serif;
      letter-spacing: -0.04em;
    }

    .glass-card {
      background: linear-gradient(180deg, rgba(15, 28, 52, 0.78), rgba(9, 18, 34, 0.92));
      border: 1px solid var(--line);
      box-shadow: var(--shadow-card);
      backdrop-filter: blur(18px);
    }

    .primary-btn,
    .secondary-btn {
      border: 0;
      border-radius: 999px;
      padding: 14px 22px;
      cursor: pointer;
      transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease;
    }

    .primary-btn {
      color: #051120;
      font-weight: 800;
      background: linear-gradient(135deg, var(--accent) 0%, #d4f7ff 100%);
      box-shadow: 0 16px 40px rgba(76, 200, 255, 0.24);
    }

    .secondary-btn {
      color: var(--text-primary);
      font-weight: 700;
      background: rgba(255, 255, 255, 0.02);
      border: 1px solid rgba(169, 183, 208, 0.28);
    }

    .primary-btn:hover,
    .secondary-btn:hover {
      transform: translateY(-2px);
    }

    .primary-btn:hover {
      box-shadow: 0 22px 48px rgba(76, 200, 255, 0.28);
    }

    .secondary-btn:hover {
      border-color: rgba(131, 225, 255, 0.38);
      background: rgba(255, 255, 255, 0.05);
    }

    .fade-up {
      animation: fadeUp 0.8s ease both;
    }

    .fade-up-delay {
      animation: fadeUp 1s ease 0.12s both;
    }

    @keyframes fadeUp {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @media (max-width: 768px) {
      .site-section {
        width: min(var(--max-width), calc(100% - 24px));
      }
    }
  `;

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = globalTheme;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_OAUTH_CLIENT_ID}>
      <Router>
        <div className="page-shell">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/visa-agent" element={<VisaAgent />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/omniseller/delete-account" element={<DeleteAccount />} />
          </Routes>
        </div>
      </Router>
    </GoogleOAuthProvider>
  );
}

export default App;
